import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSocket } from '../../contexts/SocketContext'
import { QueueItem } from './QueueItem'
import { apiGet } from '../../utils/api'
import { QueueChat } from '../../hooks/useQueue'

/** Map raw snake_case chat data to camelCase QueueChat */
function mapRawChat(c: any): QueueChat {
  return {
    id: c.id || c.chat_id,
    customerId: c.customer_id || c.customerId || '',
    customerName: c.customer_name || c.customerName || 'Unknown',
    customerEmail: c.customer_email || c.customerEmail || '',
    plan: c.customer_plan_tier || c.plan || 'free',
    priority: c.priority || 'medium',
    tags: Array.isArray(c.tags) ? c.tags : (typeof c.tags === 'string' && c.tags ? JSON.parse(c.tags) : []),
    status: c.status || 'queued',
    assignedAgentId: c.agent_id || c.assignedAgentId,
    assignedAgentName: c.agent_name || c.assignedAgentName,
    createdAt: c.created_at || c.createdAt || new Date().toISOString(),
    lastMessageAt: c.last_message_at || c.lastMessageAt || c.created_at || c.createdAt || new Date().toISOString(),
    waitTime: c.wait_time_seconds || c.waitTime || 0,
    messageCount: c.message_count || c.messageCount || 0,
    firstCustomerMessage: c.first_customer_message || c.firstCustomerMessage || undefined,
    latestMessage: c.latest_message
      ? { content: c.latest_message.body || c.latest_message.content || '', sender: c.latest_message.sender_type || c.latest_message.sender || 'customer' }
      : undefined,
    channel: c.channel || 'widget',
    visitorName: c.visitor_name || c.visitorName || undefined,
    companyName: c.company_name || c.companyName || undefined,
    companyLogoUrl: c.company_logo_url || c.companyLogoUrl || undefined,
    companyCountry: c.company_country || c.companyCountry || undefined,
  }
}

export const MyChatsView: React.FC = () => {
  const navigate = useNavigate()
  const { socket } = useSocket()

  const [chats, setChats] = useState<QueueChat[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [tab, setTab] = useState<'open' | 'closed'>('open')
  const tabRef = useRef(tab)
  tabRef.current = tab
  const fetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchMyChats = useCallback(async () => {
    try {
      setLoading(true)
      const response = await apiGet<{ chats: any[]; total: number } | any[]>('/queue/mine', { status: tabRef.current })
      const rawChats = Array.isArray(response) ? response : (response.chats || [])
      const mapped = rawChats.map(mapRawChat)
      mapped.sort((a, b) => {
        const aTime = new Date(a.lastMessageAt || a.createdAt).getTime()
        const bTime = new Date(b.lastMessageAt || b.createdAt).getTime()
        return bTime - aTime
      })
      setChats(mapped)
    } catch {
      // silent
    } finally {
      setLoading(false)
    }
  }, [])

  const debouncedFetch = useCallback(() => {
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current)
    fetchTimerRef.current = setTimeout(() => fetchMyChats(), 300)
  }, [fetchMyChats])

  useEffect(() => {
    fetchMyChats()
  }, [fetchMyChats, tab])

  // Socket listeners for real-time updates
  useEffect(() => {
    if (!socket) return

    const onChatAssigned = () => debouncedFetch()
    const onChatClosed = () => debouncedFetch()
    const onQueueUpdate = () => debouncedFetch()
    const onChatTransferred = () => debouncedFetch()
    const onNewMessage = () => debouncedFetch()

    socket.on('chat_assigned', onChatAssigned)
    socket.on('chat_closed', onChatClosed)
    socket.on('queue_update', onQueueUpdate)
    socket.on('chat_transferred', onChatTransferred)
    socket.on('new_message', onNewMessage)

    return () => {
      socket.off('chat_assigned', onChatAssigned)
      socket.off('chat_closed', onChatClosed)
      socket.off('queue_update', onQueueUpdate)
      socket.off('chat_transferred', onChatTransferred)
      socket.off('new_message', onNewMessage)
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current)
    }
  }, [socket, debouncedFetch])

  const handleSelectChat = (chatId: string) => {
    navigate(`/my-chats/${chatId}`)
  }

  const filteredChats = chats.filter((chat) => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      chat.customerName?.toLowerCase().includes(q) ||
      chat.customerEmail?.toLowerCase().includes(q) ||
      chat.tags.some((t) => t.toLowerCase().includes(q))
    )
  })

  return (
    <div className="h-full flex flex-col">
      {/* Header — title + tabs live in the Chats shell above; here: search,
          refresh, and the Open/Closed sub-filter. */}
      <div className="px-6 pt-4 pb-4 bg-surface border-b border-border">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-txt-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              placeholder="Search my chats..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border bg-surface text-txt-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-colors"
            />
          </div>
          <button
            onClick={() => fetchMyChats()}
            className="p-2 rounded-lg text-txt-secondary hover:text-txt-primary hover:bg-surface-hover border border-border transition-colors shrink-0"
            title="Refresh"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin-slow' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
            </svg>
          </button>
        </div>

        {/* Open / Closed tabs */}
        <div className="flex gap-1 mt-4 p-0.5 rounded-lg bg-surface-secondary w-fit">
          {([['open', 'Open'], ['closed', 'Closed']] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                tab === key ? 'bg-surface text-brand-600 shadow-sm' : 'text-txt-secondary hover:text-txt-primary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Chat list */}
      <div className="flex-1 overflow-y-auto">
        {loading && chats.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center gap-3 text-txt-secondary">
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="text-sm">Loading your chats...</span>
            </div>
          </div>
        ) : filteredChats.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-6">
            <div className="w-16 h-16 rounded-2xl bg-surface-tertiary flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-txt-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-txt-primary mb-1">{tab === 'closed' ? 'No closed chats yet' : 'No open chats assigned to you'}</h3>
            <p className="text-xs text-txt-tertiary max-w-[240px]">
              {tab === 'closed' ? 'Chats you resolve will be archived here.' : 'Pick up a chat from the shared queue and it will appear here.'}
            </p>
          </div>
        ) : (
          <div>
            {filteredChats.map((chat) => (
              <QueueItem
                key={chat.id}
                chat={chat}
                isSelected={false}
                onClick={() => handleSelectChat(chat.id)}
                onPickup={() => {}} // Already assigned — no pickup needed
                isPickingUp={false}
                isAlerting={false}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
